import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Tooltip, TooltipTrigger, TooltipContent, TooltipProvider,
} from "./ui/tooltip";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";

/**
 * Tiny coloured dot near the avatar that surfaces the remaining lifetime
 * of the current refresh JWT. Hover for a tooltip; auto-toasts a one-time
 * warning when < 12 hours remain so users can extend before they lose state.
 *
 * Buckets: green ≥ 24h | amber 1–24h | red < 1h
 * Polls /auth/me every 5 minutes (paused when tab is hidden).
 */
const ONE_HOUR_MS = 60 * 60 * 1000;
const ONE_DAY_MS = 24 * ONE_HOUR_MS;
const POLL_MS = 5 * 60 * 1000;
const WARN_THRESHOLD_MS = 12 * ONE_HOUR_MS;

function formatRemaining(ms) {
  if (ms <= 0) return "expired";
  const m = Math.round(ms / 60000);
  if (m < 60) return `${m} min`;
  const h = Math.round(ms / ONE_HOUR_MS);
  if (h < 48) return `${h}h`;
  return `${Math.round(ms / ONE_DAY_MS)}d`;
}

function bucketOf(ms) {
  if (ms < ONE_HOUR_MS) return "red";
  if (ms < ONE_DAY_MS) return "amber";
  return "green";
}

const COLOURS = {
  green: { dot: "bg-[#00E676]", ring: "ring-[#00E676]/40", text: "text-[#00E676]" },
  amber: { dot: "bg-[#FFD60A]", ring: "ring-[#FFD60A]/40", text: "text-[#FFD60A]" },
  red:   { dot: "bg-[#FF3B30]", ring: "ring-[#FF3B30]/50", text: "text-[#FF3B30]" },
};

export default function SessionIndicator() {
  const { user, refreshMe } = useAuth();
  const [now, setNow] = useState(() => Date.now());
  const [warned, setWarned] = useState(false);

  // Re-render every minute so the tooltip stays accurate without re-fetching.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60 * 1000);
    return () => clearInterval(t);
  }, []);

  // Poll /auth/me every 5 min (skip when tab is hidden) so the surfaced
  // expiry stays in sync if the user logs in/out from another device.
  useEffect(() => {
    if (!user) return undefined;
    const t = setInterval(() => {
      if (typeof document !== "undefined" && document.hidden) return;
      refreshMe?.();
    }, POLL_MS);
    return () => clearInterval(t);
  }, [user, refreshMe]);

  const extend = useCallback(async () => {
    try {
      await api.post("/auth/refresh");
      await refreshMe?.();
      toast.success("Session extended");
      setWarned(false);
    } catch {
      toast.error("Couldn't extend — please log in again.");
    }
  }, [refreshMe]);

  if (!user?.session_expires_at) return null;

  const expMs = new Date(user.session_expires_at).getTime();
  const remaining = expMs - now;
  const bucket = bucketOf(remaining);
  const c = COLOURS[bucket];

  // Once-per-session warning toast for amber/red.
  if (!warned && remaining > 0 && remaining < WARN_THRESHOLD_MS) {
    setWarned(true);
    setTimeout(() => {
      toast(
        `Session expires in ${formatRemaining(remaining)}`,
        { description: "Stay signed in?", action: { label: "Extend", onClick: extend } }
      );
    }, 0);
  }

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span aria-label="Session status"
            data-testid="session-indicator"
            className={`relative h-2.5 w-2.5 rounded-full ${c.dot} ring-2 ${c.ring} block`}>
            <span className={`absolute inset-0 rounded-full ${c.dot} animate-ping opacity-40`} />
          </span>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="bg-[#131313] border-white/10 text-white text-xs">
          <div className={`font-bold ${c.text}`}>Session active</div>
          <div className="text-zinc-400">Expires in {formatRemaining(remaining)}</div>
          <div className="text-[10px] text-zinc-500 mt-1">Open menu to extend</div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * "Extend session" menu item — render this INSIDE a DropdownMenu so the user
 * can extend on demand without nesting a button inside the avatar trigger
 * (which would be invalid HTML and break React hydration).
 */
export function ExtendSessionMenuItem({ onExtend }) {
  const { user, refreshMe } = useAuth();
  const handle = useCallback(async () => {
    try {
      await api.post("/auth/refresh");
      await refreshMe?.();
      toast.success("Session extended");
      onExtend?.();
    } catch {
      toast.error("Couldn't extend — please log in again.");
    }
  }, [refreshMe, onExtend]);
  if (!user?.session_expires_at) return null;
  const remaining = new Date(user.session_expires_at).getTime() - Date.now();
  return (
    <button
      onClick={handle}
      data-testid="extend-session-menu-item"
      className="w-full px-2 py-1.5 text-xs text-left text-zinc-400 hover:text-[#00E676] flex items-center justify-between"
    >
      <span>Session expires in {formatRemaining(remaining)}</span>
      <span className="text-[10px] uppercase tracking-wider">Extend</span>
    </button>
  );
}
